import { Box, Typography, alpha } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  TrackChanges as GoalsIcon,
  SmartToy as CoachIcon,
  Phone as CallsIcon,
  Groups as CommunityIcon,
  Folder as ResourcesIcon,
  Assessment as ReportsIcon,
  Person as ProfileIcon,
  Extension as IntegrationsIcon,
  AdminPanelSettings as AdminIcon,
  Info as InfoIcon,
  Description as DocumentIcon,
  Chat as ConversationIcon,
} from "@mui/icons-material";
import { ReactNode } from "react";

import { SearchResult, SearchResultType } from "@/api/generated.schemas";
import { NavigationItem } from "@/utils/navigationSearchItems";

interface SearchResultItemProps {
  result: SearchResult | NavigationItem;
  isSelected: boolean;
  onSelect: () => void;
  query: string;
}

/**
 * Get icon for a result based on its type.
 */
function getResultIcon(result: SearchResult | NavigationItem): ReactNode {
  // Navigation items have an icon property
  if ("icon" in result) {
    const iconMap: Record<string, ReactNode> = {
      Dashboard: <DashboardIcon fontSize="small" />,
      School: <SchoolIcon fontSize="small" />,
      TrackChanges: <GoalsIcon fontSize="small" />,
      SmartToy: <CoachIcon fontSize="small" />,
      Phone: <CallsIcon fontSize="small" />,
      Groups: <CommunityIcon fontSize="small" />,
      Folder: <ResourcesIcon fontSize="small" />,
      Assessment: <ReportsIcon fontSize="small" />,
      Person: <ProfileIcon fontSize="small" />,
      Extension: <IntegrationsIcon fontSize="small" />,
      AdminPanelSettings: <AdminIcon fontSize="small" />,
      Info: <InfoIcon fontSize="small" />,
    };
    return iconMap[result.icon] || <DocumentIcon fontSize="small" />;
  }

  // API results have a type property
  const typeIconMap: Record<SearchResultType, ReactNode> = {
    [SearchResultType.navigation]: <DashboardIcon fontSize="small" />,
    [SearchResultType.goal]: <GoalsIcon fontSize="small" />,
    [SearchResultType.milestone]: <GoalsIcon fontSize="small" />,
    [SearchResultType.task]: <GoalsIcon fontSize="small" />,
    [SearchResultType.conversation]: <ConversationIcon fontSize="small" />,
    [SearchResultType.transcript]: <CallsIcon fontSize="small" />,
    [SearchResultType.resource]: <ResourcesIcon fontSize="small" />,
    [SearchResultType.course]: <SchoolIcon fontSize="small" />,
    [SearchResultType.enrollment]: <SchoolIcon fontSize="small" />,
  };

  return typeIconMap[result.type as SearchResultType] || <DocumentIcon fontSize="small" />;
}

/**
 * Get display type label for a result.
 */
function getTypeLabel(result: SearchResult | NavigationItem): string {
  if ("icon" in result) {
    return "Page";
  }

  const typeLabelMap: Record<SearchResultType, string> = {
    [SearchResultType.navigation]: "Page",
    [SearchResultType.goal]: "Goal",
    [SearchResultType.milestone]: "Milestone",
    [SearchResultType.task]: "Task",
    [SearchResultType.conversation]: "AI Coach",
    [SearchResultType.transcript]: "Transcript",
    [SearchResultType.resource]: "Resource",
    [SearchResultType.course]: "Training",
    [SearchResultType.enrollment]: "Enrollment",
  };

  return typeLabelMap[result.type as SearchResultType] || result.type;
}

/**
 * Highlight query matches in text.
 */
function highlightMatch(text: string, query: string): ReactNode {
  if (!query || query.length < 2) {
    return text;
  }

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  const testRegex = new RegExp(`^${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i");

  return parts.map((part, i) =>
    testRegex.test(part) ? (
      <Box component="mark" key={i} sx={{ bgcolor: "warning.light", borderRadius: 0.5, px: 0.25 }}>
        {part}
      </Box>
    ) : (
      part
    )
  );
}

/**
 * Single search result item component.
 */
export function SearchResultItem({ result, isSelected, onSelect, query }: SearchResultItemProps) {
  const title = result.title;
  const snippet = "snippet" in result ? result.snippet : undefined;
  const typeLabel = getTypeLabel(result);

  return (
    <Box
      role="option"
      aria-selected={isSelected}
      onClick={onSelect}
      sx={(theme) => ({
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        bgcolor: isSelected ? alpha(theme.palette.secondary.main, 0.08) : "transparent",
        "&:hover": {
          bgcolor: alpha(theme.palette.secondary.main, 0.04),
        },
        borderLeft: isSelected
          ? `3px solid ${theme.palette.secondary.main}`
          : "3px solid transparent",
      })}
    >
      {/* Icon */}
      <Box
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          mt: 0.25,
        })}
      >
        {getResultIcon(result)}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {highlightMatch(title, query)}
        </Typography>

        {/* Snippet */}
        {snippet && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            dangerouslySetInnerHTML={{ __html: snippet }}
          />
        )}

        {/* Type badge */}
        <Typography
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.text.disabled,
            fontSize: "0.7rem",
          })}
        >
          {typeLabel}
        </Typography>
      </Box>
    </Box>
  );
}
